// #region Imports
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import { useGetFeeSchedulesForTeamQuery } from "marketplace/teams/fee-schedules/_api/fee-schedule-api";
import { getFeeSchedulesForTeam } from "marketplace/teams/fee-schedules/_stores/fee-schedule-redux-slice";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedFeeSchedule } from "work/masked-fields";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  paddingBottom: theme.spacing(8),
}));
const ScheduleList = styled(List)(({ theme }) => ({
  flex: 1,
  maxWidth: theme.spacing(50),
  minHeight: 0,
  overflowY: "auto",
}));
const ScheduleHeading = styled(ListSubheader)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  gap: theme.spacing(0.5),
  lineHeight: 2.2,
}));
const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  paddingTop: theme.spacing(1),
}));
const AddSelectedButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  marginTop: theme.spacing(2),
}));
const ButtonsContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
}));
// #endregion

// #region Props/Types
export type TeamPublishedRatesListProps = {
  className?: string;
  /** The marketplace team whose profile advertises these rates — `VendorTeam.marketplaceId`. */
  teamId: Guid;
  /** Already on the proposal's fee schedule: offered, but not selectable a second time. */
  disabledCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | MaskedFeeSchedule;
  /**
   * Who the proposal's vendor team actually is.
   *
   * A published rate names the person who bills it, and that assignment is most of what makes an
   * advertised rate worth pulling in. It only survives if that person is on the proposal's team —
   * see `keepAssignableTeamMember`.
   */
  proposalTeamMembers: readonly EntityMember[];
  onCategoriesSelected: (categories: FeeScheduleCategory[]) => void;
  onCancel: () => void;
};
// #endregion

/**
 * Keeps a published rate's assignee only when the proposal's own team contains them.
 *
 * The marketplace team and the proposal's vendor team are usually the same people, but they are not
 * the same object and nothing keeps them in step: the team can be edited on either side after the
 * proposal was drafted. An assignee the proposal's team does not contain would name somebody the
 * fee schedule cannot bill, so the rate arrives unassigned and the count below says how many did.
 *
 * Module scope deliberately: called from inside the component's `map`, where a `??` chain would
 * cost the whole component its React Compiler memoization.
 */
function keepAssignableTeamMember(
  category: FeeScheduleCategory,
  proposalTeamMembers: readonly EntityMember[],
): EntityMember | null {
  const assignee = category.teamMember;
  if (!assignee) return null;
  const isOnTheProposalsTeam = proposalTeamMembers.some((member) => member.isEqualTo(assignee));
  return isOnTheProposalsTeam ? assignee : null;
}

/** Module scope for the same reason as above. */
function categoryIsAlreadyOnTheProposal(
  category: FeeScheduleCategory,
  disabledCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | MaskedFeeSchedule,
): boolean {
  if (Masked.isMasked(disabledCategories)) return false;
  return disabledCategories.some((existing) => existing.name.isEqualTo(category.name));
}

/**
 * The rates a marketplace team advertises on its profile, offered to a proposal's fee schedule.
 *
 * A client can reach a vendor team's published rates from the team's profile but had no way to put
 * them on the proposal they were drafting against that team (issue #5113) — they had to read the
 * profile in one place and retype the rates in another. This lists them where the proposal already
 * offers the firm's own catalog, and hands what was chosen to the same merge the catalog uses, so a
 * pulled rate is an ordinary added category: same duplicate rule, same redline, same history.
 *
 * Read-only source. Nothing here writes to the team's published schedules — a proposal negotiates
 * its own copy of a rate, and editing it must never reach back into what the team advertises.
 */
export default function TeamPublishedRatesList(props: Readonly<TeamPublishedRatesListProps>) {
  const { className, teamId, disabledCategories, proposalTeamMembers, onCategoriesSelected, onCancel } = props;

  // #region State
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
  // #endregion

  // #region Hooks
  const { isError, isLoading } = useGetFeeSchedulesForTeamQuery({ teamId: teamId.value });
  const feeSchedulesJson = useSelector(getFeeSchedulesForTeam(teamId));

  // A fresh array each render would reach the memo below and the list's keys. Memoized deliberately
  // so a selection is not lost to an unrelated re-render of the drawer around it.
  const feeSchedules = useMemo(
    () => (feeSchedulesJson ? FeeSchedule.fromJSONArray(feeSchedulesJson) : []),
    [feeSchedulesJson],
  );

  /** Every published category, flattened, so a selection can be resolved back by id. */
  const categoriesById = useMemo(() => {
    const byId = new Map<string, FeeScheduleCategory>();
    feeSchedules.forEach((schedule) => {
      schedule.categories.forEach((category) => byId.set(category.id.value, category));
    });
    return byId;
  }, [feeSchedules]);
  // #endregion

  const publishedCategoryCount = categoriesById.size;

  function toggleCategory(categoryId: string) {
    setSelectedCategoryIds((previous) => {
      const next = new Set(previous);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }

  /**
   * Hands over copies, not the published categories themselves.
   *
   * Each copy takes a new id: the proposal's fee schedule keys its items by id, and reusing the
   * marketplace category's would tie a negotiated item to the advertised one it came from — two
   * proposals pulling the same rate would then collide on identity.
   */
  function handleAddSelectedClicked() {
    const chosen: FeeScheduleCategory[] = [];
    let unassignedCount = 0;

    selectedCategoryIds.forEach((categoryId) => {
      const published = categoriesById.get(categoryId);
      if (!published) return;

      const teamMember = keepAssignableTeamMember(published, proposalTeamMembers);
      if (published.teamMember && !teamMember) unassignedCount += 1;

      chosen.push(
        new FeeScheduleCategory({
          id: Guid.generate(),
          name: published.name,
          description: published.description ?? null,
          fee: published.fee ?? null,
          billingCode: published.billingCode ?? null,
          teamMember,
          teamMemberIsMasked: false,
        }),
      );
    });

    onCategoriesSelected(chosen);
    if (unassignedCount > 0) {
      enqueueSnackbar(
        `${unassignedCount} rate${unassignedCount === 1 ? "" : "s"} arrived unassigned: the person ` +
        "the team advertises them against is not on this proposal's team.",
        { variant: "info" },
      );
    }
  }

  // #region Render
  if (isLoading) {
    return (
      <MainContainer className={className}>
        <Loader />
      </MainContainer>
    );
  }

  return (
    <MainContainer className={className}>
      {isError && (
        <EmptyText variant="body2">
          Couldn&apos;t load this team&apos;s published rates. Please try again.
        </EmptyText>
      )}
      {!isError && publishedCategoryCount < 1 && (
        <EmptyText variant="body2">
          This team hasn&apos;t published any rates on its marketplace profile.
        </EmptyText>
      )}
      {publishedCategoryCount > 0 && (
        <ScheduleList dense subheader={<li />}>
          {feeSchedules.map((schedule) => (
            <li key={schedule.id.value}>
              <ul style={{ padding: 0 }}>
                <ScheduleHeading disableSticky={false}>
                  {schedule.name}
                  {schedule.tags.map((tag) => (
                    <Chip key={tag.value} label={tag.value} size="small" variant="outlined" />
                  ))}
                </ScheduleHeading>
                {schedule.categories.map((category) => {
                  const alreadyOnProposal = categoryIsAlreadyOnTheProposal(category, disabledCategories);
                  return (
                    <ListItemButton
                      key={category.id.value}
                      disabled={alreadyOnProposal}
                      onClick={() => toggleCategory(category.id.value)}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          disableRipple
                          tabIndex={-1}
                          checked={selectedCategoryIds.has(category.id.value)}
                          disabled={alreadyOnProposal}
                          slotProps={{ input: { "aria-label": category.name.value } }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={category.name.value}
                        secondary={
                          alreadyOnProposal
                            ? "Already on this proposal"
                            : [category.feeString, category.description?.value]
                              .filter(Boolean)
                              .join(" — ")
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </ul>
            </li>
          ))}
        </ScheduleList>
      )}
      <ButtonsContainer>
        <AddSelectedButton
          variant="contained"
          color="primary"
          disabled={selectedCategoryIds.size < 1}
          onClick={handleAddSelectedClicked}
        >
          Add Selected
        </AddSelectedButton>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      </ButtonsContainer>
    </MainContainer>
  );
  // #endregion
}
